// Concrete project math: slabs/pads and cylindrical post holes.
// Slab L × W in the active unit (ft or m). Thickness ALWAYS in inches for
// user-friendliness (people say "4 inch slab", not "0.33 ft slab").
// Post hole diameter ALWAYS in inches; depth in the active unit.
// Flags bulk delivery > 1 cu yd — usually cheaper than bags.
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface Slab {
  length: number       // ft (imperial) or m (metric)
  width: number
  thicknessIn: number  // ALWAYS in inches regardless of unit system
}

export interface PostHole {
  diameterIn: number   // ALWAYS in inches
  depth: number        // ft (imperial) or m (metric)
  count: number
}

export type BagSize = 60 | 80

export interface ConcreteInputs {
  unit: UnitSystem
  slabs: Slab[]
  postHoles: PostHole[]
  bagSize: BagSize
  wastePct: number
  includeMesh: boolean
}

export interface ConcreteResult {
  cubicFeet: number           // raw, no waste
  cubicFeetTotal: number      // waste-adjusted (imperial volume display)
  cubicYards: number          // waste-adjusted (bulk-delivery convenience figure)
  cubicMeters: number         // waste-adjusted (metric volume display)
  bags: number
  totalLbs: number
  slabAreaSqFt: number
  slabAreaM2: number          // metric mirror of slabAreaSqFt
  meshRolls: number
  useBulk: boolean            // recommend ready-mix when >= 1 cu yd
  lines: AccessoryLine[]
}

export const DEFAULT_CONCRETE: ConcreteInputs = {
  unit: 'imperial',
  slabs: [{ length: 10, width: 10, thicknessIn: 4 }],
  postHoles: [],
  bagSize: 80,
  wastePct: 5,
  includeMesh: true,
}

const BAG_YIELD: Record<BagSize, number> = { 60: 0.45, 80: 0.6 }  // cu ft per bag
const MESH_ROLL_SQFT = 150                                          // 4 ft × 37.5 ft roll

export function computeConcrete(i: ConcreteInputs, locale: Locale = 'en'): ConcreteResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let slabVolCuFt = 0
  let slabAreaSqFt = 0

  for (const s of i.slabs ?? []) {
    const lFt = toFeet(s.length, i.unit)
    const wFt = toFeet(s.width, i.unit)
    const tFt = Math.max(0, s.thicknessIn) / 12   // inches → feet, unit-independent
    const area = rectArea(lFt, wFt)
    slabAreaSqFt += area
    slabVolCuFt += area * tFt
  }

  let holeVolCuFt = 0
  for (const h of i.postHoles ?? []) {
    const rFt = Math.max(0, h.diameterIn) / 12 / 2   // inches → feet → radius
    const dFt = toFeet(Math.max(0, h.depth), i.unit)
    const count = Math.max(1, Math.round(h.count))
    holeVolCuFt += Math.PI * rFt * rFt * dFt * count
  }

  const totalCuFt = slabVolCuFt + holeVolCuFt
  const withWaste = applyWaste(totalCuFt, i.wastePct)
  const cubicYards = withWaste / 27

  const bags = totalCuFt > 0 ? ceilTo(withWaste / BAG_YIELD[i.bagSize], 1) : 0
  const totalLbs = bags * i.bagSize

  const meshRolls = i.includeMesh && slabAreaSqFt > 0
    ? ceilTo(slabAreaSqFt / MESH_ROLL_SQFT, 1)
    : 0

  const useBulk = cubicYards >= 1

  const lines: AccessoryLine[] = []

  if (i.includeMesh && slabAreaSqFt > 0) {
    lines.push({
      item: 'wire mesh reinforcement concrete', label: tr('Wire mesh reinforcement', 'Treillis métallique d’armature'), kind: 'consumable',
      qty: meshRolls, unit: 'roll',
      note: tr(`~${MESH_ROLL_SQFT} sq ft per roll · overlap seams by 6 in · lift off ground before pour`, `~${MESH_ROLL_SQFT} pi² par rouleau · chevaucher les joints de 6 po · soulever du sol avant la coulée`),
    })
  }

  if (useBulk) {
    lines.push({
      item: 'ready mix concrete delivery', label: tr('Ready-mix delivery (see note)', 'Livraison de béton prêt à l’emploi (voir la note)'), kind: 'tool',
      note: tr(`Job is ≈ ${round(cubicYards, 2)} cu yd — ready-mix delivery is typically faster and cheaper than ${bags} bags above 1 cu yd.`, `Le projet fait ≈ ${round(cubicYards, 2)} verges cubes — le béton prêt à l’emploi est généralement plus rapide et plus économique que ${bags} sacs au-delà de 1 verge cube.`),
    })
  }

  lines.push({ item: 'concrete rebar', label: tr('Rebar', 'Barre d’armature'), kind: 'tool', note: tr('For structural slabs and footings — size to local building code.', 'Pour les dalles structurales et les semelles — dimensionner selon le code du bâtiment local.'), optional: true })
  lines.push({ item: 'concrete mixing hoe paddle drill', label: tr('Mixing hoe or paddle mixer', 'Houe à mélange ou malaxeur à palette'), kind: 'tool', note: tr('Drill paddle mixer is faster for 5+ bags.', 'Le malaxeur à palette sur perceuse est plus rapide pour 5 sacs ou plus.') })
  lines.push({ item: 'concrete form boards 2x4 stakes', label: tr('Form boards + stakes (2×4)', 'Planches de coffrage + piquets (2×4)'), kind: 'tool', note: tr('Frame the slab perimeter; oil boards before pouring.', 'Encadrez le pourtour de la dalle; huilez les planches avant la coulée.'), optional: true })
  lines.push({ item: 'concrete bull float trowel', label: tr('Bull float or mag trowel', 'Taloche ou taloche en magnésium'), kind: 'tool', note: tr('For finishing a flat surface.', 'Pour finir une surface plane.') })
  lines.push({ item: 'heavy work gloves rubber boots', label: tr('Heavy gloves + rubber boots', 'Gants épais + bottes de caoutchouc'), kind: 'consumable', optional: true, note: tr('Wet concrete is highly alkaline — protect skin and feet.', 'Le béton frais est très alcalin — protégez la peau et les pieds.') })

  // Metric mirrors. 1 cu ft = 0.028316846592 m³; 1 sq ft = 0.09290304 m².
  const cubicMeters = withWaste * 0.028316846592
  const slabAreaM2 = slabAreaSqFt * 0.09290304

  return {
    cubicFeet: round(totalCuFt, 2),
    cubicFeetTotal: round(withWaste, 2),
    cubicYards: round(cubicYards, 2),
    cubicMeters: round(cubicMeters, 2),
    bags,
    totalLbs,
    slabAreaSqFt: round(slabAreaSqFt, 0),
    slabAreaM2: round(slabAreaM2, 1),
    meshRolls,
    useBulk,
    lines,
  }
}
